import { watchEffect, useRoute, onMounted, watch, reactive, computed, defineComponent, getCurrentInstance, useContext, ref, } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { formatPath, SPORTS_SECTIONS } from './data';
import { ADFOX_CODES_DESKTOP, ADFOX_CODES_MOBILE } from '~/components/Adfox';
import { createGridChunks, getArticlesSize } from '~/composables';
import { presets as rubricPresets } from '~/composables/grid/rubric';
import { useDuck } from '~/services';
import { cachePage, useAsyncRef, createHead, sendAnalytics } from '~/utils';
/**
 * Duck service.
 */
const duck = useDuck();
/**
 * Columns
 */
export const columns = {
    desktop: 2,
    desktopMedium: 2,
    desktopWide: 3,
    mobile: 1,
    mobileMedium: 1,
    mobileWide: 1,
    tablet: 2,
};
export const TABS = Object.keys(SPORTS_SECTIONS).map((path) => {
    return {
        path,
        title: SPORTS_SECTIONS[path].title,
        theme: 'lighter',
        value: SPORTS_SECTIONS[path].slug,
    };
});
function getSectionFromPath(input) {
    return SPORTS_SECTIONS[formatPath(input) || '/'];
}
function getPageTitleFromSection(section) {
    return section.slug === 'sport-all' ? 'Forbes Sport' : section.title;
}
export default defineComponent({
    name: 'PageSport',
    setup() {
        const context = useContext();
        const viewport = context.$viewport;
        const route = useRoute();
        /**
         * Instance.
         */
        const instance = getCurrentInstance();
        const isDone = useAsyncRef(instance, 'isDone');
        const currentTab = useAsyncRef(instance, 'currentTab');
        const slug = useAsyncRef(instance, 'slug');
        const listsVisibility = useAsyncRef(instance, 'listsVisibility');
        const tabs = reactive({
            currentTab,
            tabs: computed(() => {
                const defaultColor = viewport.isLessThan('tablet') ? 'light' : 'lighter';
                return TABS.map((tab) => {
                    return {
                        ...tab,
                        theme: tab.value === slug.value ? 'yellow' : defaultColor,
                    };
                });
            }),
            change(tab) {
                const { path, title, value, theme = '' } = tab;
                tabs.currentTab = {
                    path,
                    theme,
                    title,
                    value,
                };
                slug.value = value;
                history.pushState(null, '', new URL('/sport' + formatPath(path), location.href));
            },
        });
        /**
         * Articles.
         */
        const articles = useAsyncRef(instance, 'articles');
        const presets = useAsyncRef(instance, 'presets');
        const referer = ref('');
        /**
         * Current preset.
         */
        const articlesCards = computed(() => getArticlesSize(viewport.breakpoint, {
            columns,
            presets: presets.value,
        }));
        /**
         * Grids.
         */
        const grids = computed(() => createGridChunks(articles.value, articlesCards.value));
        const pageOffset = useAsyncRef(instance, 'pageOffset');
        const pageHead = useAsyncRef(instance, 'head');
        /**
         * Fetch articles task.
         */
        const fetchArticlesTask = useTask(function* (signal) {
            if (fetchArticlesTask.isRunning || isDone.value) {
                return;
            }
            signal.addEventListener('abort', () => duck.abortRequest('getPubTax'));
            try {
                if (!slug.value) {
                    return;
                }
                let newArticles = [];
                const { data: list } = yield duck.pub.getPubListById(slug.value, {
                    'list[limit]': String(articlesCards.value),
                    'list[offset]': String(articles.value.length * (pageOffset.value + 1)),
                });
                newArticles = list.articles.map((article) => article.data);
                if (newArticles.length < articlesCards.value) {
                    isDone.value = true;
                }
                if (!newArticles.length) {
                    return;
                }
                articles.value = [...articles.value, ...newArticles];
            }
            catch (error) {
                console.error(error);
            }
            finally {
                sendAnalytics.call(context, {
                    path: location.pathname,
                    referer: referer.value,
                    title: document.title,
                    url: location.href,
                });
                referer.value = location.href;
            }
        });
        /**
         * Bind CardAdfox attrs
         * @param gridKey
         */
        function bindCardAdfox(gridKey) {
            if (viewport.isLessThan('tablet')) {
                const BANNERS_COUNT = 6;
                let options = ADFOX_CODES_MOBILE['300x250_6'];
                if (gridKey < BANNERS_COUNT) {
                    options = ADFOX_CODES_MOBILE[`300x250_${gridKey + 1}`];
                }
                return {
                    options,
                    size: {
                        height: 0,
                        width: 1,
                    },
                    type: 'ad-mobile',
                };
            }
            return {
                options: {
                    ...ADFOX_CODES_DESKTOP.billboard_2,
                },
                size: {
                    height: 0,
                    width: 3,
                },
                type: 'ad',
            };
        }
        function getListsVisibility(gridKey) {
            return gridKey === 0
                ? listsVisibility.value
                : Object.keys(listsVisibility.value).reduce((items, key) => {
                    items[key] = false;
                    return items;
                }, {});
        }
        function handleGridIntersect(gridKey) {
            gridKey = gridKey + 1 + pageOffset.value;
            history.replaceState(null, '', gridKey > 1 ? `?page=${gridKey}` : location.href);
        }
        onMounted(() => {
            watch(slug, () => {
                articles.value = [];
                isDone.value = false;
                fetchArticlesTask.cancelAll();
                fetchArticlesTask.perform();
            });
            watch(slug, () => {
                const section = getSectionFromPath(location.pathname);
                if (section) {
                    pageHead.value.title = getPageTitleFromSection(section);
                }
            });
            watchEffect(() => {
                const section = getSectionFromPath(route.value.path);
                if (!section) {
                    return;
                }
                slug.value = section.slug;
                pageHead.value.title = getPageTitleFromSection(section);
            });
        });
        return {
            articlesCards,
            bindCardAdfox,
            columns,
            fetchArticlesTask,
            getListsVisibility,
            grids,
            handleGridIntersect,
            isDone,
            listsVisibility,
            pageHead,
            tabs,
        };
    },
    async asyncData(context) {
        const { app, env, error, req, res, route } = context;
        try {
            let isDone = false;
            const routeSlug = formatPath(route.path) || '/';
            const section = SPORTS_SECTIONS[routeSlug];
            if (!section) {
                return error({ statusCode: 404 });
            }
            const currentTab = TABS.find((t) => t.value === section.slug);
            if (!currentTab) {
                return error({ statusCode: 404 });
            }
            const presets = rubricPresets;
            const limit = getArticlesSize(app.$viewport.breakpoint, {
                columns,
                presets,
            });
            const pageNumber = Number(route.query.page) || 0;
            const pageOffset = pageNumber > 0 ? pageNumber - 1 : 0;
            let articles = [];
            const title = getPageTitleFromSection(section);
            const { data: list } = await duck.pub.getPubListById(section.slug, {
                'list[limit]': String(limit),
                'list[offset]': String(limit * pageOffset),
            });
            if (!list.articles?.length) {
                throw new Error('Not found');
            }
            if (list.articles.length < limit) {
                isDone = true;
            }
            articles = list.articles.map((article) => article.data);
            const lists = {
                'sport-interesting-today': {
                    'slider:interesting-today': true,
                    'slider-mobile:interesting-today': true,
                },
                'sport-partner': {
                    'slider:rubric-partner': true,
                    'slider-mobile:rubric-partner': true,
                },
            };
            const listsData = {};
            const listsVisibility = {};
            const tasks = await Promise.allSettled(Object.keys(lists).map(async (id) => {
                const { data } = await duck.pub.getPubListById(id);
                return data;
            }));
            for (const task of tasks) {
                let cardsKeys;
                if (task.status === 'fulfilled') {
                    cardsKeys = lists[task?.value?.slug || ''];
                    if (!cardsKeys) {
                        continue;
                    }
                    for (const cardKey of Object.keys(cardsKeys)) {
                        listsData[cardKey] = task.value;
                        listsVisibility[cardKey] = true;
                    }
                }
                else {
                    const splittedUrl = task?.reason?.url?.split('/');
                    if (!splittedUrl.length) {
                        continue;
                    }
                    const slug = splittedUrl[splittedUrl.length - 1];
                    cardsKeys = lists[slug];
                    if (!cardsKeys) {
                        continue;
                    }
                    for (const cardKey of Object.keys(cardsKeys)) {
                        listsVisibility[cardKey] = false;
                    }
                }
            }
            if (process.server) {
                const [firstArticle] = articles;
                if (firstArticle) {
                    const date = firstArticle.changed || firstArticle.time || firstArticle.created;
                    cachePage({ date, req, res });
                }
            }
            const head = {
                canonical: env.HOST + route.path,
                title,
                url: env.HOST + route.path,
            };
            context.$page = app.$page = {
                title,
            };
            return {
                $page: context.$page,
                currentTab,
                slug: section.slug,
                articles,
                head,
                isDone,
                listsData,
                listsVisibility,
                presets,
                pageOffset,
                title,
            };
        }
        catch (e) {
            console.error(e);
            error({ statusCode: 404 });
        }
    },
    head() {
        return createHead(this.head);
    },
});
